import { privatePoolForToken } from "../../joinsplit-v2/privatePools";
import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import type { BaseStoredNote, BaseStatusMessage } from "../types";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { friendlyError } from "../utils/errors";
import { executeFreshPoolDeposit } from "../../joinsplit-v2/deposit";
import { withFlatJoinSplitV2WalletLock } from "../../joinsplit-v2/walletLock";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { DEPOSIT_PROGRESS_LABELS } from "../../joinsplit-v2/depositProgress";
import {
  DEPOSIT_MAX_QUOTE_AGE_MS,
  type NativeDepositMaxQuote,
} from "../../joinsplit-v2/depositMax";

interface UseBaseShieldOperationParams {
  signer: ethers.Signer | null;
  spendingKeys: any;
  notes: BaseStoredNote[];
  setNotes: React.Dispatch<React.SetStateAction<BaseStoredNote[]>>;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  setStatus: (status: BaseStatusMessage | null) => void;
}

export function useBaseShieldOperation({
  signer,
  spendingKeys,
  notes,
  setNotes,
  addLog,
  setStatus,
}: UseBaseShieldOperationParams) {
  const activeDepositRef = useRef<AbortController | null>(null);
  const [depositProgress, setDepositProgress] = useState("");

  useEffect(
    () => () => {
      activeDepositRef.current?.abort();
      activeDepositRef.current = null;
    },
    [signer, spendingKeys],
  );

  const handleShield = useCallback(
    async (
      amountStr: string,
      recipientMPK?: bigint,
      recipientInboxPublicKeyHex?: string,
      token?: TokenInfo,
      maxQuote?: NativeDepositMaxQuote,
    ) => {
      if (!signer || !spendingKeys) {
        setStatus({ type: "error", message: "Connect wallet first" });
        return;
      }

      const decimals = token?.decimals ?? 18;
      const symbol = token?.symbol ?? "ETH";
      const tokenAddress = token?.address ?? ethers.ZeroAddress;
      const selectedTokenType = token?.tokenType ?? "erc20";
      const tokenType =
        selectedTokenType === "vnft" ? "erc721" : selectedTokenType;
      const tokenSubID = token?.tokenSubID;
      const isWrappedNft = tokenType === "erc721" || tokenType === "erc1155";

      try {
        if (
          maxQuote &&
          Date.now() - maxQuote.quotedAt > DEPOSIT_MAX_QUOTE_AGE_MS
        )
          throw new Error(
            "The MAX estimate has expired. Select MAX again to refresh the amount.",
          );
        if (selectedTokenType === "vnft") {
          setStatus({
            type: "error",
            message:
              "vSTEALTH notes are pool-side and cannot be shielded from wallet balances.",
          });
          return;
        }
        const amount = ethers.parseUnits(amountStr || "0", decimals);
        if (amount <= 0n) {
          setStatus({ type: "error", message: "Enter a valid amount" });
          return;
        }
        if (
          (tokenType === "erc721" || tokenType === "erc1155") &&
          !EVM_CONFIG.NFT_EVM_ENABLED
        ) {
          setStatus({
            type: "error",
            message: "NFT shielding is disabled in this environment",
          });
          return;
        }
        if (tokenType === "erc721" && amount !== 1n) {
          setStatus({
            type: "error",
            message: "ERC721 shield amount must be exactly 1",
          });
          return;
        }
        if (
          (tokenType === "erc721" || tokenType === "erc1155") &&
          (!tokenSubID || tokenSubID.trim() === "")
        ) {
          setStatus({ type: "error", message: "Missing NFT token ID" });
          return;
        }
        if (tokenType !== "erc20" || selectedTokenType !== "erc20") {
          throw new Error(
            "The fresh JoinSplit pool currently supports fungible ETH/ERC20 deposits only",
          );
        }
        if (activeDepositRef.current) {
          throw new Error("A private deposit is already in progress");
        }
        const controller = new AbortController();
        activeDepositRef.current = controller;
        const startedAt = performance.now();
        let previousProgress = "";
        try {
          setDepositProgress("Preparing transaction…");
          addLog("Deposit preparation started", "info");
          const signerAddress = await signer.getAddress();
          const result = await withFlatJoinSplitV2WalletLock({
            chainId: EVM_CONFIG.CHAIN_ID,
            poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
            accountAddress: signerAddress,
            signal: controller.signal,
            run: () =>
              executeFreshPoolDeposit({
                signer,
                confirmationProvider: getBaseReadProvider(),
                expectedChainId: EVM_CONFIG.CHAIN_ID,
                poolAddress: privatePoolForToken(tokenAddress),
                tokenAddress,
                tokenDecimals: decimals,
                amount,
                maxQuote,
                recipientMpk: recipientMPK ?? spendingKeys.mpk,
                senderMpk: spendingKeys.mpk,
                spendingKeys,
                notes,
                journalSpendingKey: spendingKeys.spendingKey,
                recipientInboxPublicKeyHex,
                recipientPrivateHistory: {
                  type: "receive",
                  amount: amountStr,
                  symbol,
                  timestamp: Date.now(),
                },
                signal: controller.signal,
                confirmations: EVM_CONFIG.JOINSPLIT_V2.FINALITY_BLOCKS,
                onProgress: (step) => {
                  if (activeDepositRef.current !== controller) return;
                  const label = DEPOSIT_PROGRESS_LABELS[step];
                  setDepositProgress(label);
                  if (step !== previousProgress) {
                    previousProgress = step;
                    addLog(
                      `Deposit (${Math.round((performance.now() - startedAt) / 1000)}s): ${label}`,
                      "info",
                    );
                  }
                },
              }),
          });
          if (result.spentCommitments?.length) {
            const spent = new Set(
              result.spentCommitments.map((commitment) =>
                commitment.toLowerCase(),
              ),
            );
            setNotes((current) =>
              current.map((stored) =>
                spent.has(stored.note.commitment.toLowerCase())
                  ? { ...stored, spent: true }
                  : stored,
              ),
            );
          }
          addLog(
            result.mode === "deposit-merge"
              ? `Deposit merged into private balance in tree ${result.treeIndex}`
              : `Fresh-pool deposit confirmed in tree ${result.treeIndex}`,
            "success",
            result.transactionHash,
          );
          return {
            success: true as const,
            txHash: result.transactionHash,
            note: result.note,
            journalIds: [result.journalId],
            mode: result.mode,
            spentCommitments: result.spentCommitments,
          };
        } finally {
          if (activeDepositRef.current === controller) {
            activeDepositRef.current = null;
          }
        }
      } catch (error) {
        const candidateHash = (error as { txHash?: unknown })?.txHash;
        const submittedHash =
          typeof candidateHash === "string" &&
          ethers.isHexString(candidateHash, 32)
            ? candidateHash
            : undefined;
        const message = submittedHash
          ? `Deposit submitted; confirmation or recovery is pending. Refresh your private balance before trying another deposit. Transaction: ${submittedHash}. ${friendlyError(error)}`
          : friendlyError(error);
        setStatus({ type: "error", message });
        addLog(
          submittedHash ? message : `Shield failed: ${message}`,
          submittedHash ? "warning" : "error",
          submittedHash,
        );
        return {
          success: false as const,
          txHash: undefined as string | undefined,
          note: undefined as any,
        };
      }
    },
    [signer, spendingKeys, notes, setNotes, setStatus, addLog],
  );

  return { handleShield, depositProgress, setDepositProgress };
}
